import { Gauge, Filter, FileCode, Database, Lock, Eye } from "lucide-react";

export const ApiSecurityBestPractices = () => {
  const practices = [
    {
      icon: Gauge,
      title: "Rate Limiting",
      description: "Protect against brute force and DDoS attacks by limiting requests per user, IP, or API key.",
      tips: [
        "Implement tiered limits (100 req/min for free, 1000 for paid)",
        "Use sliding window algorithms for smoother limiting",
        "Return 429 with Retry-After header",
        "Consider different limits for different endpoints",
      ],
      color: "text-chart-1",
      bgColor: "bg-chart-1/10",
    },
    {
      icon: Filter,
      title: "Input Validation",
      description: "Never trust user input. Validate, sanitize, and parameterize all inputs before processing.",
      tips: [
        "Use schema validation (Zod, Joi, JSON Schema)",
        "Implement strict type checking",
        "Set maximum length limits on all string inputs",
        "Use parameterized queries — never string concatenation",
      ],
      color: "text-chart-2",
      bgColor: "bg-chart-2/10",
    },
    {
      icon: FileCode,
      title: "Response Sanitization",
      description: "Control what data leaves your API to prevent accidental exposure of sensitive information.",
      tips: [
        "Never return full database objects",
        "Use DTOs/response schemas to shape output",
        "Remove internal IDs, timestamps, and metadata",
        "Implement field-level access control",
      ],
      color: "text-chart-4",
      bgColor: "bg-chart-4/10",
    },
    {
      icon: Database,
      title: "SQL Injection Prevention",
      description: "SQL injection remains a top attack vector. Parameterized queries are your primary defense.",
      tips: [
        "Always use parameterized queries or ORMs",
        "Never concatenate user input into SQL strings",
        "Use stored procedures with input validation",
        "Implement least-privilege database accounts",
      ],
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      icon: Lock,
      title: "Authorization (BOLA Prevention)",
      description: "Verify the user has permission to access the specific resource, not just any resource of that type.",
      tips: [
        "Check object ownership on every request",
        "Use UUIDs instead of sequential IDs",
        "Implement row-level security in the database",
        "Log and alert on authorization failures",
      ],
      color: "text-chart-3",
      bgColor: "bg-chart-3/10",
    },
    {
      icon: Eye,
      title: "Logging & Monitoring",
      description: "You can't protect what you can't see. Comprehensive logging is essential for incident response.",
      tips: [
        "Log all authentication attempts and failures",
        "Track unusual patterns (high error rates, odd hours)",
        "Set up real-time alerting for suspicious activity",
        "Retain logs for at least 90 days",
      ],
      color: "text-muted-foreground",
      bgColor: "bg-muted/50",
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Essential API Security Practices
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        These six practices form the foundation of secure API development. Implement them all 
        to significantly reduce your attack surface.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {practices.map((practice, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border p-6 hover:shadow-lg transition-shadow"
          >
            <div className={`w-12 h-12 rounded-xl ${practice.bgColor} flex items-center justify-center mb-4`}>
              <practice.icon className={`w-6 h-6 ${practice.color}`} />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">{practice.title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{practice.description}</p>
            <ul className="space-y-2">
              {practice.tips.map((tip, tipIndex) => (
                <li key={tipIndex} className="text-sm text-foreground flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-2xl border border-primary/20 p-6 sm:p-8">
        <h3 className="text-lg font-semibold text-foreground mb-3">Security Checklist Before Deployment</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "All endpoints require authentication",
            "Rate limiting is configured",
            "Input validation on all parameters",
            "SQL queries are parameterized",
            "Sensitive data is encrypted at rest",
            "HTTPS is enforced everywhere",
            "CORS is properly configured",
            "Error messages don't leak info",
            "Logging and monitoring active",
          ].map((item, index) => (
            <label key={index} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input type="checkbox" className="rounded border-border" />
              {item}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
};
